import { NavigationGuard } from "vue-router";
import { auth } from "@/utils/LtkAuth0";
import { getCurrentEnv } from "@/utils/util";
import Authentication from ".";

const $authentication = new Authentication();
const wechatCodeItem = `wechatCode:${getCurrentEnv()}`;
const clearWechatCode = () => {
  sessionStorage.removeItem(wechatCodeItem);
  // Discard codes persisted by the previous flow, including Auth0 callback codes.
  localStorage.removeItem("wechatCode");
};
const setWechatCode = (code: string) => {
  clearWechatCode();
  sessionStorage.setItem(wechatCodeItem, JSON.stringify({
    code,
    expiresAt: Date.now() + 5 * 60 * 1000
  }));
};
const getWechatCode = () => {
  localStorage.removeItem("wechatCode");
  const stored = sessionStorage.getItem(wechatCodeItem);
  if (!stored) return '';
  try {
    const { code, expiresAt } = JSON.parse(stored);
    if (typeof code === "string" && code && typeof expiresAt === "number" && expiresAt > Date.now()) {
      return code;
    }
  } catch {
    // A malformed or expired code requires fresh WeChat authorization.
  }
  clearWechatCode();
  return '';
};

/**
 * Implicit On navigation guard requires the next route to specify
 * that it does not require auth before checking authentication status
 * of the user.
 */
const redirectRoute = async (config: any) => {
  const {
    isAuthenticated,
    isLambdaAuthorized,
    accessToken,
    idTokenClaims,
    to,
    next
  } = config;
  if (!isLambdaAuthorized) {
    await $authentication.lambdaAuthorize(
      accessToken,
      idTokenClaims,
      "login"
    );
  }
  if (!isAuthenticated) {
    return next({ path: "/login" });
  } else if (isAuthenticated && to.name === "Login") {
    return next({ path: "/logged-in" });
  } else {
    return next();
  }
};

const navigationGuard: NavigationGuard = async (to, from, next) => {
  const code = to.query.code;
  const openID = $authentication.openID;
  // Auth0 and WeChat both return a `code`, but only the WeChat entry code
  // belongs in the WeChat exchange. Read the destination route, not the old URL.
  if (to.path !== "/login/callback" && typeof code === "string" && code) {
    if (!openID) setWechatCode(code);
    const query = { ...to.query };
    delete query.code;
    // Keep the code out of restored Auth0 targets and subsequent navigations.
    return next({ path: to.path, query, hash: to.hash, replace: true });
  }
  const fullPath = to.fullPath;

  if (!to.meta.requiresAuth && to.name !== "Login") {
    if (!to.meta.requiresAuth) {
      if (to.fullPath === "/" || !to.name) {
        return next({ path: "login" });
      }
      return next();
    }
    return next();
  }

  // when there's an openID in the local storage
  try {
    const accessToken = await auth.getAccessToken() || '';
    const idTokenClaims = (await auth.getIdTokenClaims())?.__raw;
    const isAuthenticated = await auth.isAuthenticated();
    if (openID) {
      clearWechatCode();
      const isLambdaAuthorized = await $authentication.checkLoggedIn(
        openID,
        accessToken,
        idTokenClaims
      );
      return redirectRoute({
        isAuthenticated,
        accessToken,
        idTokenClaims,
        isLambdaAuthorized,
        to,
        next
      });
    } else {
      // first time ever interact with the OA
      const code = getWechatCode();
      if (!code) return next({ path: "/error" });
      // WeChat codes are single use. Never retry a code after an exchange attempt.
      clearWechatCode();
      let openID: string;
      try {
        openID = await $authentication.getOpenID(code, accessToken);
      } catch {
        return next({ path: "/error" });
      }
      $authentication.setOpenID(openID);
      const isLambdaAuthorized = await $authentication.checkLoggedIn(
        openID,
        accessToken,
        idTokenClaims
      );
      return redirectRoute({
        isAuthenticated,
        accessToken,
        idTokenClaims,
        isLambdaAuthorized,
        to,
        next
      });
    }
  } catch (error) {
    if (
      error instanceof Error && (
        (error as Error & { error?: string }).error === "login_required" ||
        (error as Error & { error?: string }).error === "consent_required" ||
        error.message.toLowerCase() === "login required"
      )
    ) {
      await auth.login({
        options: {
          appState: {
            target: `${fullPath}`
          },
          authorizationParams: { ui_locales: "zh-Hans", lang: "zh-Hans" }
        },
        next: `${fullPath}`
      });
    }
  }
};

export default navigationGuard;
